import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { InfoTip } from "@/components/InfoTip";
import { useL } from "@/lib/useL";

// "Mes cartes": list of flashcard decks with today's due count.
export default function Flashcards() {
  const L = useL();
  const [decks, setDecks] = useState(null);
  const load = () => api.get("/flashcards").then(({ data }) => setDecks(data)).catch(() => setDecks([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm(L("Supprimer ce jeu de cartes ?", "Delete this card deck?"))) return;
    try {
      await api.delete(`/flashcards/${id}`);
      load();
    } catch {
      toast.error(L("Échec de la suppression.", "Could not delete."));
    }
  };

  const due = (decks || []).reduce((s, d) => s + d.due_count, 0);
  const tip = L(
    <><b>Mes cartes</b> : tous vos jeux de cartes question / réponse. « À revoir » indique les cartes prévues aujourd'hui par la révision espacée. « Partagé » : le jeu est accessible aux élèves par lien ou QR code.</>,
    <><b>My cards</b>: all your question / answer decks. « To review » shows the cards due today by spaced repetition. « Shared »: pupils can open the deck by link or QR code.</>,
  );
  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="flashcards-page">
      <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] mb-2">{L("Mes cartes", "My cards")}</div>
      <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>{L("Cartes de révision", "Flashcards")} <InfoTip testid="flashcards-page-info" content={tip} /></h1>
      <p className="text-muted-foreground mb-8" data-testid="flashcards-due-total">
        {due > 0 ? L(`${due} carte(s) à revoir aujourd'hui.`, `${due} card(s) to review today.`) : L("Rien à revoir aujourd'hui. Revenez demain !", "Nothing to review today. Come back tomorrow!")}{" "}
        {L("Créez un jeu depuis n'importe quel résumé (bouton « Cartes de révision »).", "Create a deck from any summary (« Flashcards » button).")}
      </p>
      {decks && decks.length === 0 && (
        <div className="border border-dashed border-border p-8 text-center text-muted-foreground" data-testid="flashcards-empty">
          {L("Aucun jeu de cartes pour l'instant. Faites un résumé, puis cliquez sur « Cartes de révision ».", "No decks yet. Make a summary, then click « Flashcards ».")}
        </div>
      )}
      <ul className="space-y-3">
        {(decks || []).map((d, i) => (
          <li key={d.id} className="border border-border p-4 flex items-center gap-4" data-testid={`flashcards-deck-${i}`}>
            <Layers className="w-5 h-5 text-[#002FA7] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{d.title}</div>
              <div className="text-xs text-muted-foreground">
                {d.total} {L("cartes", "cards")} · {d.due_count > 0 ? <b className="text-[#002FA7]">{d.due_count} {L("à revoir", "to review")}</b> : L("à jour", "up to date")}{d.is_public ? ` · ${L("partagé", "shared")}` : ""}
              </div>
            </div>
            <Link to={`/cartes/${d.id}`} data-testid={`flashcards-deck-open-${i}`}
              className="font-mono uppercase tracking-[0.16em] text-[11px] px-4 py-2 bg-[#002FA7] text-white hover:bg-[#00227A] transition-colors">
              {L("Réviser", "Review")}
            </Link>
            <button type="button" onClick={() => remove(d.id)} aria-label={L("Supprimer", "Delete")} data-testid={`flashcards-deck-delete-${i}`}
              className="text-muted-foreground hover:text-[#FF2A00] transition-colors"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}
